import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Lock, Truck } from "lucide-react";
import { toast } from "sonner";
import { Btn } from "@/components/account/Shell";
import api, { formatApiError } from "@/lib/api";

/**
 * The strip that sits under the top bar on every back-office screen: is this shop running
 * 13th Ave's stock or its own? Loud and red when it is, because half the shop is locked.
 */
export default function ConsignBanner({ data, reload }) {
    const [busy, setBusy] = useState(false);
    const [confirm, setConfirm] = useState(false);
    const c = data?.consignment;
    if (!c?.eligible) return null;

    const yours = Math.round((c.rate ?? 0.2) * 100);
    const rooms = Object.values(c.rooms || {});
    const locked = rooms.filter((r) => r.consigned);
    const mine = rooms.filter((r) => r.private);

    const flip = async (on) => {
        setBusy(true);
        try {
            const { data: res } = await api.post("/store/consignment", { enabled: on });
            toast.success(res.note);
            setConfirm(false);
            reload();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    if (!c.enabled) return (
        <div className="border-b border-[#d0d0d0] bg-[#fafafa]" data-testid="consign-banner-off">
            <div className="mx-auto flex max-w-[110rem] flex-wrap items-center gap-x-3 gap-y-1 px-3 py-1.5">
                <span className="border border-[#4a4a4a] px-1.5 py-px text-[11px] font-bold uppercase tracking-[0.06em] text-[#1a1a1a]">
                    Consignment program · off
                </span>
                <p className="text-[13px] text-[#1a1a1a]">
                    <span className="font-semibold">We print it. We pack it. We ship it. You do nothing but sell.</span>
                    {" "}Switch it on and 13th Ave's whole catalog stocks your shelves — you keep {yours}% of every
                    sale plus whatever you mark it up.
                </p>
                <span className="ml-auto flex flex-wrap items-center gap-1.5">
                    <Link to="/account/consignment" data-testid="consign-banner-learn"
                        className="text-[12px] font-semibold text-[#1a56db] hover:underline">How it works →</Link>
                    <Btn tone="solid" disabled={busy} testid="consign-banner-on-btn" onClick={() => flip(true)}>
                        Turn consignment on
                    </Btn>
                </span>
            </div>
        </div>
    );

    return (
        <div className="border-b-2 border-[#b42318] bg-[#fef3f2]" data-testid="consign-banner-on">
            <div className="mx-auto flex max-w-[110rem] flex-wrap items-center gap-x-3 gap-y-1 px-3 py-1.5">
                <span className="border border-[#b42318] bg-[#b42318] px-1.5 py-px text-[11px] font-bold uppercase tracking-[0.06em] text-white bo-inv"
                    data-testid="consign-banner-flag">
                    Consignment program on
                </span>
                <p className="flex items-center gap-1.5 text-[13px] font-semibold text-[#1a1a1a]">
                    <Truck size={13} className="shrink-0 text-[#b42318]" />
                    13th Ave prints, packs and ships everything. You do nothing but sell.
                </p>
                <span className="flex items-center gap-1 text-[12px] font-semibold text-[#b42318]"
                    data-testid="consign-banner-locked">
                    <Lock size={11} /> {locked.length} of {rooms.length} shelves locked
                    {mine.length ? ` · ${mine.length} you print` : ""}
                </span>
                <span className="text-[12px] font-semibold text-[#1a1a1a]">
                    You keep {yours}% + your percent over
                </span>
                <span className="ml-auto flex flex-wrap items-center gap-1.5">
                    <Link to="/account/consignment" data-testid="consign-banner-settings"
                        className="inline-flex items-center gap-1 text-[12px] font-semibold text-[#1a56db] hover:underline">
                        Consignment settings <ArrowUpRight size={11} />
                    </Link>
                    {confirm ? (
                        <>
                            <span className="text-[12px] font-semibold text-[#b42318]">
                                Turn it off? Your shelves unlock and every order comes back to you to print and ship.
                            </span>
                            <Btn tone="danger" disabled={busy} testid="consign-banner-off-yes" onClick={() => flip(false)}>
                                Yes, turn it off
                            </Btn>
                            <Btn testid="consign-banner-off-no" onClick={() => setConfirm(false)}>Keep it on</Btn>
                        </>
                    ) : (
                        <Btn testid="consign-banner-off-btn" onClick={() => setConfirm(true)}>Turn it off</Btn>
                    )}
                </span>
            </div>
        </div>
    );
}
